import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { UserPlus } from "lucide-react";

export default function Register() {
  const { user, register, error } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [location, setLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  // Pre-fetch the active list of locations from the public endpoint so the
  // unauthenticated /register screen can show its picker without needing a
  // session. Falls back to the seed pair if the call fails.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/locations");
        const opts = (data?.locations || []).map((l) => String(l).trim().toUpperCase()).filter(Boolean);
        setLocations(opts.length ? opts : ["FISK", "NORTHWEST LABS"]);
      } catch {
        setLocations(["FISK", "NORTHWEST LABS"]);
      }
    })();
  }, []);

  if (user && user !== false) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await register(name, email, password, location);
    setSubmitting(false);
    if (ok) nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen relative bg-slate-900 flex items-stretch">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1762701249025-a80d32e3cf46?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
        }}
      />
      <div className="absolute inset-0 bg-slate-900/85" />
      <div className="relative w-full grid place-items-center p-4">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-6">
            <img
              src="/powermove-mark.png"
              alt="Material Tracker"
              className="h-12 w-12 rounded-sm object-contain bg-slate-950 border border-slate-800"
            />
            <div className="text-white">
              <div className="font-display text-2xl uppercase tracking-wider">Material</div>
              <div className="text-xs uppercase tracking-[0.2em] text-amber-400">Tracker</div>
            </div>
          </div>
          <div className="rg-card p-6 sm:p-8">
            <h1 className="font-display text-2xl text-slate-900 uppercase mb-1">Create account</h1>
            <p className="text-sm text-slate-600 mb-6">
              All accounts can view and edit shipments.
            </p>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  Full name
                </label>
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="register-name-input"
                  className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
                  placeholder="J. Doe"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="register-email-input"
                  className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  Location
                </label>
                <select
                  required
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  data-testid="register-location-select"
                  className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
                >
                  <option value="">Choose your location…</option>
                  {locations.map((loc) => (
                    <option key={loc} value={loc}>
                      {loc}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  Your records will be isolated to this location.
                </p>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  Password (min 6)
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="register-password-input"
                  className="rg-tap w-full border-2 border-slate-300 rounded-sm px-3 text-base bg-white focus:border-slate-900 outline-none"
                />
              </div>
              {error && (
                <div className="border-2 border-rose-600 bg-rose-50 text-rose-800 text-sm font-bold px-3 py-2 rounded-sm" data-testid="register-error">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting || !location}
                data-testid="register-submit-btn"
                className="rg-tap-xl w-full bg-slate-900 hover:bg-slate-800 text-white font-display uppercase tracking-wider rounded-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <UserPlus strokeWidth={3} className="h-5 w-5" />
                {submitting ? "Creating…" : "Create account"}
              </button>
            </form>
            <div className="mt-6 pt-6 border-t-2 border-slate-200 text-sm text-slate-700 text-center">
              Have an account?{" "}
              <Link to="/login" className="font-bold underline text-slate-900" data-testid="link-login">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
